// English source content for the science guides. Each guide body is a list of
// blocks: { p } paragraph, { h2 } subheading, { list } of { term, text } items
// (term is bolded, joined to text with an em dash by _GuideBody). This is the
// translation reference — every other locale file under content/ mirrors this
// shape exactly (see content/index.js + scripts/check-guide-content.mjs).
export default {
  index: {
    title: 'Tide and Current Guides',
    description:
      'Learn how tides and tidal currents work: tidal coefficients, flood and ebb currents, ' +
      'tidal datums, and how tide predictions are calculated.',
    subtitle:
      'Short, plain-language explainers on the science behind tide and current predictions.',
  },
  back: 'Back to all guides',
  guides: {
    'tidal-coefficients': {
      title: 'Tidal Coefficients Explained',
      description:
        'What tidal coefficients mean: the 20 to 120 scale, how spring and neap tides relate to ' +
        'the Moon, and why a higher coefficient brings a bigger tidal range.',
      blurb:
        'The 20 to 120 scale that summarizes how large a tide will be, and how the Moon drives it.',
      body: [
        {
          p: 'A tidal coefficient is a single number that summarizes how large a tide will be on a given day. It is widely used in France and other countries that follow the French hydrographic tradition. The coefficient is dimensionless and normally runs from about 20 to 120: the larger the number, the greater the range between high and low water, and the stronger the associated tidal currents.',
        },
        { h2: 'The 20 to 120 scale' },
        {
          p: "The coefficient compares the day's tidal range to a long-term reference. Several points on the scale have conventional meanings:",
        },
        {
          list: [
            { term: '20', text: 'the smallest tides, near the minimum astronomical range.' },
            { term: '45', text: 'an average neap tide, when the range is small.' },
            { term: '70', text: 'a mean tide, roughly the midpoint between neaps and springs.' },
            { term: '95', text: 'an average spring tide, when the range is large.' },
            { term: '120', text: 'the largest tides, near the maximum astronomical range.' },
          ],
        },
        { h2: 'Why it changes: the Moon' },
        {
          p: 'Coefficients rise and fall on the roughly two-week spring-neap cycle. Spring tides, with high coefficients, occur a day or two after each new and full Moon, when the Sun and Moon pull in line and their tidal forces add together. Neap tides, with low coefficients, occur near the first and last quarter Moons, when the Sun and Moon are at right angles and partly cancel.',
        },
        {
          p: "The Moon's distance matters too. When a spring tide coincides with perigee (the Moon's closest approach to Earth), the coefficient reaches its highest values. The very largest tides tend to cluster near the spring and autumn equinoxes.",
        },
        { h2: 'What a coefficient does not tell you' },
        {
          p: 'The coefficient describes the astronomical range only. It does not include the effect of weather: wind and atmospheric pressure can raise or lower the actual water level regardless of the coefficient. It is also a property of the tide as a whole for that day, not a height in metres or feet at any single location.',
        },
      ],
    },
    'tidal-currents': {
      title: 'Flood, Ebb, and Slack: How Tidal Currents Work',
      description:
        'How tidal currents work: flood and ebb direction, slack water at the turn, current speed ' +
        'in knots and set, and why slack water does not always line up with high and low tide.',
      blurb:
        'Flood, ebb, and slack water, and why the strongest current is not always at high tide.',
      body: [
        {
          p: 'Tidal currents are the horizontal movement of water that accompanies the vertical rise and fall of the tide. Where the tide height tells you how deep the water is, the current tells you which way it is flowing and how fast. Currents matter most in channels, inlets, and estuaries, where they can run at several knots.',
        },
        { h2: 'Flood, ebb, and slack' },
        {
          list: [
            {
              term: 'Flood current',
              text: 'water flowing inshore or up an estuary, generally while the tide is rising.',
            },
            {
              term: 'Ebb current',
              text: 'water flowing seaward or down an estuary, generally while the tide is falling.',
            },
            {
              term: 'Slack water',
              text: 'the brief pause when the current is near zero as it turns between flood and ebb.',
            },
          ],
        },
        {
          p: 'Current speed is measured in knots (nautical miles per hour). Its direction, called the set, is given as a compass bearing: the direction the water is flowing toward.',
        },
        { h2: 'Slack water is not always at high tide' },
        {
          p: 'A common assumption is that the current stops at high and low water. That is only true in some places. The timing depends on how the tide wave behaves locally. Where the tide acts like a progressive wave, as in a long open channel, the strongest flood and ebb occur near high and low water, and slack falls near the midpoint. Where it acts like a standing wave, as at the head of a closed bay, the reverse is true: the fastest current is near mid-tide and slack water occurs near high and low water. Local current tables are the reliable guide.',
        },
        { h2: 'Reversing and rotary currents' },
        {
          p: 'In narrow channels the current is usually reversing: it runs one way on the flood, then turns and runs back on the ebb. In open coastal water the current is often rotary instead, changing direction continuously through the compass over a tidal cycle rather than reversing along a single line.',
        },
      ],
    },
    'tidal-datums': {
      title: 'Tidal Datums and Tide Heights',
      description:
        'What a tide height is measured from: chart datum, Lowest Astronomical Tide (LAT), Mean ' +
        'Lower Low Water (MLLW), and why datums differ between countries.',
      blurb: 'Chart datum, LAT, MLLW, and what the number in a tide height actually means.',
      body: [
        {
          p: 'Every tide height is a measurement above a reference level called a datum. When a tide table says the water will reach 1.8 metres, it means 1.8 metres above the local tidal datum: not above the seabed and not above sea level in the everyday sense. Knowing the datum is what makes a height meaningful.',
        },
        { h2: 'What a tidal datum is' },
        {
          p: 'A tidal datum is a reference elevation defined by a particular stage of the tide, averaged over a long period: conventionally about 19 years, which smooths out the 18.6-year cycle in the Moon’s orbit. Because it is an average of real observations, a datum is specific to a location and to the authority that computed it.',
        },
        { h2: 'Chart datum and common references' },
        {
          p: 'Nautical charts use a low-water datum so that the actual depth is almost always at least the charted depth. This chart datum is the zero from which both charted depths and tide heights are reckoned. Different countries adopt different datums:',
        },
        {
          list: [
            {
              term: 'LAT (Lowest Astronomical Tide)',
              text: 'the lowest level predictable under average weather; used by the United Kingdom and many other nations.',
            },
            {
              term: 'MLLW (Mean Lower Low Water)',
              text: "the average of the lower of each day's two low waters; the standard chart datum in the United States.",
            },
            {
              term: 'MSL (Mean Sea Level)',
              text: 'the long-term average water level, used mainly as a reference for land elevations rather than for charts.',
            },
          ],
        },
        {
          p: 'High-water datums such as Mean High Water (MHW) and Mean Higher High Water (MHHW) are used for the opposite purpose, for example to define a shoreline or a bridge clearance.',
        },
        { h2: 'Why heights are not directly comparable' },
        {
          p: "Because datums differ, a height at one station cannot be compared directly with a height at another unless both use the same reference. A 0.5 metre low water measured from LAT is not the same water level as a 0.5 metre low water measured from MLLW. This is also why the same physical tide can be described by different numbers in different countries' tables.",
        },
      ],
    },
    'how-tide-predictions-work': {
      title: 'How Tide Predictions Are Calculated',
      description:
        'How tides are predicted: gravitational forcing from the Moon and Sun, harmonic ' +
        'constituents such as M2 and S2, and why observed water levels differ from the ' +
        'astronomical prediction.',
      blurb:
        'Harmonic analysis, the constituents behind the tide, and why weather makes reality differ.',
      body: [
        {
          p: "Tides can be predicted years in advance because they are driven by the motions of the Moon and Sun, which are known with great precision. The tide at a place is the ocean's response to that gravitational forcing, shaped by local coastline and depth. The method that turns astronomy into a tide table is called harmonic analysis.",
        },
        { h2: 'The tide as a sum of constituents' },
        {
          p: 'Harmonic analysis treats the tide as the sum of many simple waves, each called a constituent. Every constituent has a frequency fixed by astronomy (how the Earth, Moon, and Sun move relative to one another) while its amplitude and timing are found by fitting the constituents to a period of real water-level observations at that location. The main constituents include:',
        },
        {
          list: [
            {
              term: 'M2',
              text: 'the principal lunar semidiurnal, period 12.42 hours; usually the largest.',
            },
            { term: 'S2', text: 'the principal solar semidiurnal, period 12.00 hours.' },
            { term: 'N2', text: "a lunar constituent from the Moon's elliptical orbit." },
            {
              term: 'K1 and O1',
              text: 'the main diurnal constituents, which make one high water a day larger than the other in many places.',
            },
          ],
        },
        {
          p: 'The familiar spring-neap cycle emerges naturally from this: M2 and S2 have slightly different periods, so they drift in and out of step, reinforcing every 14.8 days to make springs and partly cancelling to make neaps.',
        },
        { h2: 'Predicting forward' },
        {
          p: 'Once the amplitude and phase of each constituent are known for a location, predicting the tide is simply a matter of evaluating the sum at any future time. The astronomical frequencies never change, so the calculation is deterministic and can run decades ahead.',
        },
        { h2: 'Why the real tide differs' },
        {
          p: 'A prediction is the astronomical tide only. It does not include the weather. Strong winds, low or high atmospheric pressure, heavy rainfall, and river flow can raise or lower the actual water level: a storm surge can add a metre or more above prediction, while high pressure and offshore winds can hold the water well below it. Predictions are therefore an excellent guide to the timing and size of the tide, but the observed level on the day can differ.',
        },
      ],
    },
  },
}
